import { __decorate } from "tslib";
import { mixins } from 'vue-class-component';
import { Component, Watch } from 'vue-property-decorator';
import PaymentOptionFormMixin from '@/helpers/vue_mixins/v2/PaymentOptionFormMixin';
import RouterParamsAppMixin from '@/helpers/vue_mixins/RouterParamsAppMixin';
import ActionButton from '@/components/action_button/Index.vue';
import FormHeader from '@/components/v2/FormHeader.vue';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import CreatePaymentOption from '@/plugins/graphql/mutations/v2/payment-option-create.gql';
import UpdatePaymentOption from '@/plugins/graphql/mutations/v2/payment-option-update.gql';
import PaymentGateways from '@/plugins/graphql/queries/v2/active-payment-gateways.gql';
import ThirdPartyApiSettingOptionUpdate from '@/plugins/graphql/mutations/v2/third-party-api-setting-option-update.gql';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { successfulCreate } from '@/helpers/mixins/LocalizationHelper';
import { PaymentOptionDetailInputModel } from '@/helpers/entities/models/PaymentOptionDetailInputModel';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import { PaymentOptionType } from '@/helpers/entities/enums/v2/PaymentOptionType';
import { selectOptionBuilder } from '@/helpers/select_option_builder';
import CreateThirdPartyApiSetting from '@/plugins/graphql/mutations/v2/third-party-api-setting-create.gql';
import UpdateThirdPartyApiSetting from '@/plugins/graphql/mutations/v2/third-party-api-setting-update.gql';
import ThirdPartySettingByAppId from '@/plugins/graphql/queries/v2/third-party-api-setting.gql';
import CleverTapEventsMixin from '@/helpers/vue_mixins/v3/CleverTapEventsMixin';
const notDeleted = (detail) => !detail.isDeleted;
const payments = ['Maya', 'GCash', 'PayMongo', 'Credit Card'];
const providers = ['paymongo', 'maya'];
let PaymentOptionFormCod = class PaymentOptionFormCod extends mixins(PaymentOptionFormMixin, RouterParamsAppMixin, ViewportSizeMixin, CleverTapEventsMixin) {
    constructor() {
        super(...arguments);
        this.paymentDetails = 'Payment Option Description';
        this.form = {
            paymentName: null,
            optionDetails: [],
            apiPublicKey: '',
            apiSecretKey: '',
            providerUuid: '',
            provider: null,
            thirdPartyProvider: null,
            creditCardProvider: null,
        };
    }
    get detailsInput() {
        return this.form
            .optionDetails.filter(notDeleted)
            .map((detail) => {
            return { content: detail.content };
        });
    }
    get mayaPaymentOption() {
        return this.paymentGateway?.paymentOption?.thirdPartyProvider === 'maya';
    }
    get thirdPartyPayment() {
        return this.paymentGateway.paymentOptionType === PaymentOptionType.thirdParty;
    }
    get creditCardPayment() {
        return this.paymentGateway.paymentOptionType === PaymentOptionType.creditCard;
    }
    get paymentNames() {
        return payments;
    }
    get providerNames() {
        return providers;
    }
    // Filter deleted subcategory in vuelidate sub categories object
    // @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/reduce
    get optionDetails() {
        const collection = this.$v.form.optionDetails.$each.$iter || {};
        const result = Object.values(collection).reduce((acc, val) => {
            if (!val.$model.isDeleted) {
                acc.push(val);
            }
            return acc;
        }, []);
        return result;
    }
    get midTypes() {
        return [
            selectOptionBuilder('default', 'Default MID Provider'),
        ];
    }
    get mayaMidTypes() {
        return [
            // selectOptionBuilder('default', 'Default MID Provider'),
            // selectOptionBuilder('paymongo', 'PayMongo'),
            selectOptionBuilder('default', 'Default MID Provider'),
        ];
    }
    onPropertyChanged(value) {
        if (value !== 'default') {
            this.$apollo
                .query(this.queryThirdPartySettingByAppId(this.appUUID, this.form.provider))
                .then(({ data }) => {
                if (data.thirdPartySettingByAppId) {
                    const setting = data.thirdPartySettingByAppId;
                    this.form.providerUuid = setting.uuid;
                    this.form.apiPublicKey = setting.apiPublicKey;
                    this.form.apiSecretKey = setting.apiSecretKey;
                }
            });
        }
    }
    onPaymentGatewayChanged(val) {
        const option = val.paymentOption;
        const gateway = this.paymentGateway;
        let optionDetails = [];
        if (this.thirdPartyPayment || this.creditCardPayment) {
            this.setDefaultInput();
        }
        if (option) {
            if (option.details) {
                optionDetails = option.details.map((detail) => {
                    return new PaymentOptionDetailInputModel(detail);
                });
            }
            this.form = {
                paymentName: option.paymentName,
                provider: this.defaultThirdPartyProvider(option) || null,
                optionDetails,
                apiPublicKey: gateway?.appPaymentProvider?.apiPublicKey,
                apiSecretKey: gateway?.appPaymentProvider?.apiSecretKey,
                providerUuid: '',
            };
        }
    }
    setDefaultInput() {
        const gateway = this.paymentGateway;
        if (this.thirdPartyPayment) {
            return Object.assign(this.form, {
                paymentName: gateway.title === 'Maya' ? 'Online Payment' : gateway.title,
                provider: 'default',
                thirdPartyProvider: gateway.title.toLowerCase(),
                apiPublicKey: gateway?.appPaymentProvider?.apiPublicKey,
                apiSecretKey: gateway?.appPaymentProvider?.apiSecretKey,
                providerUuid: '',
            });
        }
        return Object.assign(this.form, {
            paymentName: gateway.title,
            provider: 'default',
            creditCardProvider: 'paymongo',
            apiPublicKey: gateway?.appPaymentProvider?.apiPublicKey,
            apiSecretKey: gateway?.appPaymentProvider?.apiSecretKey,
            providerUuid: '',
        });
    }
    defaultThirdPartyProvider(option) {
        return !option.thirdPartyApiSetting
            ? 'default'
            : option.thirdPartyProvider || option.creditCardProvider;
    }
    addOptionDetails() {
        const detail = new PaymentOptionDetailInputModel();
        this.form.optionDetails.push(detail);
    }
    onSubmit() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
            this.submit();
        }
    }
    async submit() {
        this.requestOnFlight();
        const mutation = () => {
            const option = this.paymentGateway.paymentOption;
            const uuid = this.form.providerUuid;
            if (option) {
                return this.sendUpdateMutation(option.uuid, uuid);
            }
            return this.sendCreateMutation(uuid);
        };
        await mutation()
            .catch((e) => {
            this.errorNotification(e.message);
        })
            .finally(() => {
            this.requestDone();
        });
    }
    async sendCreateMutation(providerUuid) {
        if (this.form.paymentName === 'Online Payment' && this.form.thirdPartyProvider === 'maya') {
            this.form.paymentName = 'Maya';
        }
        return this.create().then(async (response) => {
            const optionUuid = response.data.paymentOptionCreate.option.uuid;
            if (this.form.apiSecretKey && this.form.apiPublicKey && this.form.provider !== 'default') {
                await this.createOrUpdateApi(providerUuid).then(async () => {
                    await this.updateProvider(optionUuid).then(() => {
                        const msg = successfulCreate(this.label);
                        this.successNotification(msg);
                        this.paymentManagerAction('SUBMIT CREATE', this.form.paymentName);
                        this.success();
                    });
                });
            }
            else {
                const msg = successfulCreate(this.label);
                this.successNotification(msg);
                this.paymentManagerAction('SUBMIT CREATE', this.form.paymentName);
                this.success();
            }
        });
    }
    async sendUpdateMutation(optionUuid, providerUuid) {
        return this.update(optionUuid).then(async () => {
            if (this.form.apiSecretKey && this.form.apiPublicKey && this.form.provider !== 'default') {
                await this.createOrUpdateApi(providerUuid).then(async () => {
                    await this.updateProvider(optionUuid).then(() => {
                        const msg = successfulCreate(this.label);
                        this.successNotification(msg);
                        this.paymentManagerAction('SUBMIT EDIT', this.form.paymentName);
                        this.success();
                    });
                });
            }
            else {
                await this.updateProvider(optionUuid).then(() => {
                    const msg = successfulCreate(this.label);
                    this.successNotification(msg);
                    this.paymentManagerAction('SUBMIT EDIT', this.form.paymentName);
                    this.success();
                });
            }
        });
    }
    async create() {
        const vm = this;
        const paymentGatewayUuid = this.paymentGateway.uuid;
        return this.$apollo.mutate({
            mutation: CreatePaymentOption,
            variables: {
                input: {
                    appUuid: this.appUUID,
                    paymentGatewayUuid,
                    paymentName: this.form.paymentName,
                    details: this.detailsInput,
                    thirdPartyProvider: this.form.thirdPartyProvider,
                    creditCardProvider: this.form.creditCardProvider,
                },
            },
            update(cache, { data }) {
                vm.syncPaymentGatewayCache(cache, data.paymentOptionCreate.option, paymentGatewayUuid);
            },
            refetchQueries: [
                {
                    query: PaymentGateways,
                    variables: {
                        appUuid: this.appUUID,
                    },
                },
            ],
        });
    }
    async update(optionUuid) {
        return this.$apollo.mutate({
            mutation: UpdatePaymentOption,
            variables: {
                input: {
                    optionUuid,
                    paymentName: this.form.paymentName,
                    details: this.detailsInput,
                },
            },
        });
    }
    async updateProvider(optionUuid) {
        if (this.creditCardPayment || this.thirdPartyPayment) {
            return this.$apollo.mutate({
                mutation: ThirdPartyApiSettingOptionUpdate,
                variables: {
                    input: {
                        optionUuid,
                        provider: this.form.provider,
                    },
                },
            });
        }
    }
    async createOrUpdateApi(providerUuid) {
        let mutation;
        if (providerUuid) {
            mutation = this.updateApi(providerUuid);
        }
        else {
            mutation = this.createApi();
        }
        return mutation;
    }
    async createApi() {
        const vm = this;
        const paymentGatewayUuid = this.paymentGateway.uuid;
        return this.$apollo.mutate({
            mutation: CreateThirdPartyApiSetting,
            variables: {
                input: {
                    appUuid: this.appUUID,
                    apiPublicKey: this.form.apiPublicKey,
                    apiSecretKey: this.form.apiSecretKey,
                    provider: this.form.provider,
                },
            },
            update(cache, { data }) {
                vm.syncPaymentGatewayCache(cache, data.paymentOptionCreate.option, paymentGatewayUuid);
            },
        });
    }
    async updateApi(providerUuid) {
        return this.$apollo.mutate({
            mutation: UpdateThirdPartyApiSetting,
            variables: {
                input: {
                    thirdPartyApiSettingUuid: providerUuid,
                    apiPublicKey: this.form.apiPublicKey,
                    apiSecretKey: this.form.apiSecretKey,
                },
            },
        });
    }
    queryThirdPartySettingByAppId(appUuid, provider) {
        return {
            query: ThirdPartySettingByAppId,
            variables: {
                appUuid,
                provider,
            },
        };
    }
};
__decorate([
    Watch('form.provider')
], PaymentOptionFormCod.prototype, "onPropertyChanged", null);
__decorate([
    Watch('paymentGateway', { immediate: true, deep: true })
], PaymentOptionFormCod.prototype, "onPaymentGatewayChanged", null);
PaymentOptionFormCod = __decorate([
    Component({
        components: {
            ActionButton,
            BaseTextField,
            FormHeader,
        },
        validations: {
            form: {
                apiPublicKey: {
                    required: requiredIf((value) => {
                        return providers.includes(value.provider) && payments.includes(value.paymentName);
                    }),
                },
                apiSecretKey: {
                    required: requiredIf((value) => {
                        return providers.includes(value.provider) && payments.includes(value.paymentName);
                    }),
                },
                paymentName: {
                    required,
                },
                optionDetails: {
                    $each: {
                        content: {
                            required: requiredIf(notDeleted),
                        },
                    },
                },
                provider: {
                    required: requiredIf((value) => {
                        const options = ['BPI', 'Credit Card', 'Maya', 'GCash'];
                        return options.includes(value.paymentName);
                    }),
                },
            },
        },
    })
], PaymentOptionFormCod);
export default PaymentOptionFormCod;
